"use client";

import { Suspense } from "react";
import Link from "next/link";
import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import { useLang } from "@/contexts/language-context";
import { GhlBookingEmbed } from "@/components/ghl/GhlBookingEmbed";

/** Calendario de GHL (dominio white-label propio, link.usekickoff.com).
 *  Se puede cambiar sin tocar código vía NEXT_PUBLIC_GHL_BOOKING_URL — útil si
 *  se rota el calendario o se prueba otro tipo de cita. El default deja la
 *  página funcionando aunque la variable no esté configurada en el entorno.
 *
 *  ⚠️ Este host TIENE que estar en frame-src y script-src del CSP de
 *  next.config.ts o el navegador bloquea el iframe sin mensaje visible. */
const BOOKING_URL =
  process.env.NEXT_PUBLIC_GHL_BOOKING_URL ??
  "https://link.usekickoff.com/widget/booking/IJR2Gw1nxjFKcNQJveNu";

/** GHL identifica el iframe por su id para saber a quién redimensionar. */
const IFRAME_ID = `${BOOKING_URL.split("/").pop() ?? "booking"}_redy`;

function BookingEmbed({ email }: { email: string | null }) {
  const { t } = useLang();

  // Prellenar el correo: quien llega desde un correo del drip ya nos lo dio,
  // volver a pedírselo es fricción gratis. GHL acepta el prefill por query. El
  // correo viene de la cookie httpOnly que deja el proxy (nunca de la URL de
  // /demo, que verían los pixeles): lib/marketing/demoPrefill.ts.
  const src = email
    ? `${BOOKING_URL}?email=${encodeURIComponent(email)}`
    : BOOKING_URL;

  // iframe + form_embed.js + enlace de respaldo: components/ghl/GhlBookingEmbed.
  return (
    <GhlBookingEmbed
      src={src}
      iframeId={IFRAME_ID}
      title="Redy — booking"
      fallbackLabel={t("Calendar not loading? Open it in a new tab", "¿No carga el calendario? Ábrelo en otra pestaña")}
    />
  );
}

function DemoContent({ prefillEmail }: { prefillEmail: string | null }) {
  const { t } = useLang();

  return (
    <>
      <MarketingNav />
      <main className="pt-36 pb-24 px-6">
        <div className="max-w-[840px] mx-auto">
          {/* Header */}
          <div className="mb-10">
            <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
              {t("Live demo", "Demo en vivo")}
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-redy-charcoal mb-3">
              {t("See it running before you decide", "Míralo funcionando antes de decidir")}
            </h1>
            <p className="text-[15px] text-redy-secondary max-w-xl leading-relaxed">
              {t(
                "Fifteen minutes on a real terminal — your menu, your prices, your numbers. No slides, no commitment, and you keep the answers either way.",
                "Quince minutos sobre una terminal real — tu menú, tus precios, tus números. Sin presentaciones, sin compromiso, y las respuestas te las llevas igual."
              )}
            </p>
          </div>

          {/* Calendario */}
          <div className="bg-white rounded-[24px] border border-black/5 shadow-sm p-2 sm:p-4 mb-10">
            <Suspense
              fallback={
                <div className="min-h-[720px] flex items-center justify-center text-redy-secondary text-sm">
                  {t("Loading calendar…", "Cargando calendario…")}
                </div>
              }
            >
              <BookingEmbed email={prefillEmail} />
            </Suspense>
          </div>

          {/* Salida alterna, mismo peso: quien ya se decidió no debería tener
              que esperar a una llamada para empezar. */}
          <div className="border-t border-black/8 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <div className="text-[15px] font-bold text-redy-charcoal">
                {t("Rather skip the call?", "¿Prefieres saltarte la llamada?")}
              </div>
              <p className="text-[14px] text-redy-secondary mt-1">
                {t(
                  "Create your account now and set up your menu today.",
                  "Crea tu cuenta ahora y arma tu menú hoy mismo."
                )}
              </p>
            </div>
            <SignupLink email={prefillEmail} />
          </div>
        </div>
      </main>
      <MarketingFooter />
    </>
  );
}

/** El link al registro arrastra el correo para que llegue con el formulario a
 *  medio llenar (/signup ya lee ?email= y ?business=). */
function SignupLink({ email }: { email: string | null }) {
  const { t } = useLang();
  const href = email ? `/signup?email=${encodeURIComponent(email)}` : "/signup";

  return (
    <Link
      href={href}
      className="group px-8 py-4 bg-redy-charcoal text-white font-bold rounded-full shadow-lg hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
    >
      {t("Create my account", "Crear mi cuenta")}
      <span className="transition-transform group-hover:translate-x-0.5">→</span>
    </Link>
  );
}

export default function DemoClient({ prefillEmail }: { prefillEmail: string | null }) {
  return (
    <Suspense fallback={null}>
      <DemoContent prefillEmail={prefillEmail} />
    </Suspense>
  );
}
